import type { ActivityEntry, DailySummary, Goals, Meal, MealCategory, Profile } from '../types'

export const categoryLabels: Record<MealCategory, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snacks',
}

export const categoryWeights: Record<MealCategory, number> = {
  breakfast: 0.25,
  lunch: 0.3,
  dinner: 0.35,
  snack: 0.1,
}

export function getLocalDate(date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function completedYears(dateOfBirth: string, calculationDate: string): number {
  const [birthYear = 0, birthMonth = 1, birthDay = 1] = dateOfBirth.split('-').map(Number)
  const [year = 0, month = 1, day = 1] = calculationDate.split('-').map(Number)
  let age = year - birthYear
  if (month < birthMonth || (month === birthMonth && day < birthDay)) age -= 1
  return age
}

export function recommendGoals(profile: Profile, calculationDate = getLocalDate()): Goals {
  const age = completedYears(profile.dateOfBirth, calculationDate)
  const sexAdjustment = profile.sexForEnergy === 'male' ? 5 : -161
  const bmr = 10 * profile.weightKg + 6.25 * profile.heightCm - 5 * age + sexAdjustment
  const multiplier = { low: 1.2, moderate: 1.45, high: 1.7 }[profile.activityLevel]
  const baseCalories = Math.max(1_000, Math.round((bmr * multiplier) / 50) * 50)
  const proteinG = Math.round(profile.weightKg * 1.2)
  const fatG = Math.round((baseCalories * 0.25) / 9)
  const carbohydratesG = Math.max(0, Math.round((baseCalories - proteinG * 4 - fatG * 9) / 4))

  return {
    id: crypto.randomUUID(),
    baseCalories,
    proteinG,
    carbohydratesG,
    fatG,
    source: 'recommended',
    formulaVersion: 'mifflin-st-jeor-v1',
    effectiveFrom: calculationDate,
  }
}

function roundedAllocations(
  target: number,
  consumed: Record<MealCategory, number>,
): Record<MealCategory, { consumed: number; recommendedFinal: number; suggestedRemaining: number }> {
  const categories = Object.keys(categoryWeights) as MealCategory[]
  const totalConsumed = categories.reduce((sum, category) => sum + consumed[category], 0)
  if (target <= totalConsumed) {
    return Object.fromEntries(categories.map((category) => [category, {
      consumed: consumed[category],
      recommendedFinal: consumed[category],
      suggestedRemaining: 0,
    }])) as Record<MealCategory, { consumed: number; recommendedFinal: number; suggestedRemaining: number }>
  }

  let low = 0
  let high = target / Math.min(...Object.values(categoryWeights))
  for (let iteration = 0; iteration < 80; iteration += 1) {
    const lambda = (low + high) / 2
    const allocated = categories.reduce(
      (sum, category) => sum + Math.max(consumed[category], lambda * categoryWeights[category]),
      0,
    )
    if (allocated > target) high = lambda
    else low = lambda
  }

  const rawRemaining = Object.fromEntries(categories.map((category) => [
    category,
    Math.max(low * categoryWeights[category] - consumed[category], 0),
  ])) as Record<MealCategory, number>
  const totalRemaining = Math.round(target - totalConsumed)
  const floored = Object.fromEntries(categories.map((category) => [category, Math.floor(rawRemaining[category])])) as Record<MealCategory, number>
  let leftover = totalRemaining - categories.reduce((sum, category) => sum + floored[category], 0)
  const remainderOrder = [...categories].sort((a, b) => (rawRemaining[b] - floored[b]) - (rawRemaining[a] - floored[a]))
  for (const category of remainderOrder) {
    if (leftover <= 0) break
    floored[category] += 1
    leftover -= 1
  }

  return Object.fromEntries(categories.map((category) => [category, {
    consumed: Math.round(consumed[category]),
    recommendedFinal: Math.round(consumed[category] + floored[category]),
    suggestedRemaining: Math.max(0, floored[category]),
  }])) as Record<MealCategory, { consumed: number; recommendedFinal: number; suggestedRemaining: number }>
}

export function buildDailySummary(
  date: string,
  timezone: string,
  goals: Goals | null,
  allMeals: Meal[],
  allActivities: ActivityEntry[],
): DailySummary {
  const meals = allMeals.filter((meal) => meal.localDate === date && meal.status === 'confirmed')
  const activities = allActivities.filter((activity) => activity.localDate === date)
  const currentAnalyses = meals.map((meal) => meal.analyses.find((analysis) => analysis.id === meal.confirmedAnalysisId)).filter(Boolean)
  const baseTarget = goals?.baseCalories ?? 0
  const activityCalories = activities.filter((activity) => activity.affectsDailyTarget).reduce((sum, activity) => sum + activity.caloriesBurned, 0)
  const adjustedTarget = Math.round(baseTarget + activityCalories)
  const consumed = Math.round(currentAnalyses.reduce((sum, analysis) => sum + (analysis?.totals.calories ?? 0), 0))
  const macroConsumed = {
    protein: currentAnalyses.reduce((sum, analysis) => sum + (analysis?.totals.proteinG ?? 0), 0),
    carbohydrates: currentAnalyses.reduce((sum, analysis) => sum + (analysis?.totals.carbohydratesG ?? 0), 0),
    fat: currentAnalyses.reduce((sum, analysis) => sum + (analysis?.totals.fatG ?? 0), 0),
  }
  const categoryConsumed = Object.fromEntries((Object.keys(categoryWeights) as MealCategory[]).map((category) => [
    category,
    Math.round(meals.filter((meal) => meal.category === category).reduce((sum, meal) => {
      const analysis = meal.analyses.find((candidate) => candidate.id === meal.confirmedAnalysisId)
      return sum + (analysis?.totals.calories ?? 0)
    }, 0)),
  ])) as Record<MealCategory, number>
  const macroResult = (target: number | null | undefined, value: number) => ({
    target: target ?? null,
    consumed: Math.round(value),
    remaining: target == null ? null : Math.max(Math.round(target - value), 0),
  })

  return {
    date,
    timezone,
    baseTarget,
    activityCalories: Math.round(activityCalories),
    adjustedTarget,
    consumed,
    remaining: Math.max(adjustedTarget - consumed, 0),
    overTarget: Math.max(consumed - adjustedTarget, 0),
    macros: {
      protein: macroResult(goals?.proteinG, macroConsumed.protein),
      carbohydrates: macroResult(goals?.carbohydratesG, macroConsumed.carbohydrates),
      fat: macroResult(goals?.fatG, macroConsumed.fat),
    },
    mealSuggestions: roundedAllocations(adjustedTarget, categoryConsumed),
    meals,
    activities,
  }
}
